import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Tabs,
    Tab,
    Tooltip,
    Snackbar,
    Alert
} from '@mui/material';
import PageHeader from 'components/Header/Page';
import CreateSelectRuleTab from './CreateSelectRuleTab';
import ServiceMappingTab from './ServiceMappingTab';
import ConfigureRulesTab from './ConfigureRulesTab';
import ApprovalTab from './ApprovalTab';
import ActivationTab from './ActivationTab';
import { SavedRule, RuleDetailsSummary, CreateSelectRuleTabHandle, MappingRow } from './types';
import { getRuleConfigs } from 'service/approvalWorkflow';
import { mapDtoListToSavedRules } from './helpers';

const SERVICE_MAPPING_TAB_INDEX = 1;
const CONFIGURE_RULES_TAB_INDEX = 2;
const APPROVAL_TAB_INDEX = 3;
const LAST_TAB_INDEX = 4;

const EarlyWarningSystemRule = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [savedRules, setSavedRules] = useState<SavedRule[]>([]);
    const [mappings, setMappings] = useState<MappingRow[]>([]);
    const [selectedRuleId, setSelectedRuleId] = useState<number | undefined>(undefined);
    const [editRuleIdInTab0, setEditRuleIdInTab0] = useState<number | null>(null);
    const navigate = useNavigate();
    const createRuleRef = useRef<CreateSelectRuleTabHandle>(null);

    // Step-completion flags, reported up by each tab
    const [isCreateRuleComplete, setIsCreateRuleComplete] = useState(false);
    const [isServiceMappingComplete, setIsServiceMappingComplete] = useState(false);
    const [canProceedFromConfigureRules, setCanProceedFromConfigureRules] = useState(false);
    const [isApprovalComplete, setIsApprovalComplete] = useState(false);
    const [isActivationComplete, setIsActivationComplete] = useState(false);
    const [saveConfirmationOpen, setSaveConfirmationOpen] = useState(false);

    // Bumped on successful Save to remount every tab with fresh internal state
    const [wizardResetKey, setWizardResetKey] = useState(0);

    // Initial fetch of saved rule configurations so all independent tabs have data immediately
    useEffect(() => {
        let isMounted = true;
        const fetchInitialRules = async () => {
            try {
                const resp = await getRuleConfigs();
                const data = resp?.data || resp;
                if (isMounted && Array.isArray(data) && data.length > 0) {
                    const mapped = mapDtoListToSavedRules(data);
                    setSavedRules((prev) => (prev.length === 0 ? mapped : prev));
                    setSelectedRuleId((prev) => (prev !== undefined ? prev : mapped[mapped.length - 1].id));
                }
            } catch (err) {
                console.log('Failed to fetch initial rule configs in EWS index:', err);
            }
        };
        fetchInitialRules();
        return () => { isMounted = false; };
    }, []);

    useEffect(() => {
        if (!isCreateRuleComplete) {
            setIsServiceMappingComplete(false);
            setCanProceedFromConfigureRules(false);
            setIsApprovalComplete(false);
        }
    }, [isCreateRuleComplete]);

    const isConfigureRulesUnlocked = isCreateRuleComplete && isServiceMappingComplete;
    const isOnLastTab = activeTab === LAST_TAB_INDEX;

    const currentSelectedRule = useMemo(() => {
        if (savedRules.length === 0) return undefined;
        if (selectedRuleId !== undefined) {
            const found = savedRules.find((r) => r.id === selectedRuleId);
            if (found) return found;
        }
        return savedRules[savedRules.length - 1];
    }, [savedRules, selectedRuleId]);

    const activeRuleMapping = useMemo(() => {
        if (!currentSelectedRule) return undefined;
        return mappings.find((m) => m.ewsRule.toLowerCase() === currentSelectedRule.ruleName.toLowerCase() || m.id === currentSelectedRule.id);
    }, [mappings, currentSelectedRule]);

    const activeRuleDetails: Partial<RuleDetailsSummary> | undefined = useMemo(() => {
        if (!currentSelectedRule) return undefined;
        const handlerName = activeRuleMapping?.handler || currentSelectedRule.handler || '—';
        return {
            backendId: currentSelectedRule.backendId,
            ruleName: currentSelectedRule.ruleName,
            signal: currentSelectedRule.ewsSignalName,
            domainComponentLayer: `${currentSelectedRule.domain || currentSelectedRule.metric || 'Volume'} > ${currentSelectedRule.component || currentSelectedRule.dataSourceLabel || 'Ingestion'} > ${currentSelectedRule.layer || 'Bronze / All'}`,
            domain: currentSelectedRule.domain || currentSelectedRule.metric || 'Volume',
            component: currentSelectedRule.component || 'adf',
            layer: currentSelectedRule.layer || 'bronze',
            riskLevel: currentSelectedRule.riskLevel,
            status: currentSelectedRule.status || 'DRAFT',
            metricKPI: currentSelectedRule.metricKPI,
            dataSource: currentSelectedRule.dataSourceLabel || currentSelectedRule.dataSourceValue,
            detectionLogic: currentSelectedRule.detectionLogic,
            handlerAgent: handlerName,
            notes: currentSelectedRule.notes,
            baseEsQuery: currentSelectedRule.baseEsQuery,
            createdAt: new Date().toLocaleString()
        };
    }, [currentSelectedRule, activeRuleMapping]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        if (isTabDisabled(newValue)) return;
        setActiveTab(newValue);
    };

    const handleCancel = () => {
        navigate(-1);
    };

    const canAdvanceFromCurrentTab =
        (activeTab !== CONFIGURE_RULES_TAB_INDEX || canProceedFromConfigureRules) &&
        (activeTab !== APPROVAL_TAB_INDEX || isApprovalComplete);

    const handleSaveAndNext = async () => {
        if (!canAdvanceFromCurrentTab) return;
        if (activeTab === 0 && createRuleRef.current) {
            const success = await createRuleRef.current.saveCurrentFormOnAdvance();
            if (!success) return;
        }
        setActiveTab((prev) => Math.min(prev + 1, LAST_TAB_INDEX));
    };

    const handleHeaderButtonClick = async () => {
        if (isOnLastTab) {
            if (isActivationComplete) {
                setSaveConfirmationOpen(true);
                setActiveTab(0);
                setSavedRules([]);
                setSelectedRuleId(undefined);
                setMappings([]);
                setEditRuleIdInTab0(null);
                setIsCreateRuleComplete(false);
                setIsServiceMappingComplete(false);
                setCanProceedFromConfigureRules(false);
                setIsApprovalComplete(false);
                setIsActivationComplete(false);
                setWizardResetKey((prev) => prev + 1);
            }
            return;
        }
        await handleSaveAndNext();
    };

    const tabLabels = ['Create/Select Rule', 'Service Mapping', 'Configure Rules', 'Approval', 'Activation'];
    const isTabDisabled = (_index: number) => false;
    const tabDisabledReason = (_index: number) => '';

    return (
        <Box sx={{ mt: '77px' }}>
            <PageHeader
                title={[
                    { text: 'Summary', link: '/dataops/early-warning-rule' },
                    { text: 'Create EWS Rule Engine' }
                ]}
                hideIcon={true}
                childrenContainerProps={{ my: 0 }}
                headerSx={{ marginBottom: '12px', paddingTop: '12px', top: '77px' }}
            >
                {/* Hide header action buttons on Service Mapping (tab 1) and Configure Rules (tab 2) */}
                {activeTab !== 1 && activeTab !== 2 && (
                    <>
                        <Button
                            variant="text"
                            onClick={handleCancel}
                            sx={{
                                mr: 2,
                                color: '#39ACE7',
                                fontWeight: 600,
                                textTransform: 'uppercase'
                            }}
                        >
                            CANCEL
                        </Button>
                        <Tooltip title={
                            isOnLastTab
                                ? (isActivationComplete ? '' : 'Activate this rule before finishing.')
                                : (canAdvanceFromCurrentTab ? '' : 'Resolve all validation issues (or apply an authorized override) before proceeding.')
                        }>
                            <span>
                                <Button
                                    variant="contained"
                                    onClick={handleHeaderButtonClick}
                                    disabled={isOnLastTab ? !isActivationComplete : !canAdvanceFromCurrentTab}
                                    sx={{
                                        backgroundColor: '#39ACE7',
                                        color: '#fff',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        '&:hover': {
                                            backgroundColor: '#2c8ebb'
                                        },
                                        '&.Mui-disabled': { backgroundColor: '#39ACE799', color: '#fff' }
                                    }}
                                >
                                    {isOnLastTab ? 'SAVE' : 'SAVE & NEXT'}
                                </Button>
                            </span>
                        </Tooltip>
                    </>
                )}
            </PageHeader>
            <Box key={wizardResetKey} sx={{ px: { xs: 0, sm: 2 }, pt: 0 }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    textColor="inherit"
                    variant="fullWidth"
                    TabIndicatorProps={{ style: { backgroundColor: '#009BD8', height: '3px' } }}
                    sx={{
                        width: '100%',
                        minHeight: 50,
                        height: 50,
                        borderRadius: '4px 4px 0 0',
                        overflow: 'hidden',
                        '& .MuiTabs-scroller': {
                            height: 50,
                        },
                        '& .MuiTabs-flexContainer': {
                            width: '100%',
                            height: 50,
                            overflowY: 'hidden',
                        },
                        '& .MuiTabs-indicator': {
                            backgroundColor: 'white !important',
                            height: '4px !important',
                        },
                        '& .MuiTab-root': {
                            margin: '0 !important',
                            padding: '12px 0px !important',
                            color: '#F5F5F5 !important',
                            backgroundColor: '#003771 !important',
                            textTransform: 'none',
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            minHeight: 50,
                            height: 50,
                            flex: '1 1 20%',
                            width: '20% !important',
                            maxWidth: '20% !important',
                            opacity: '1 !important',
                            borderRadius: '0 !important',
                            boxSizing: 'border-box !important',
                            borderRight: '1px solid rgba(255, 255, 255, 0.4)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            '&:hover': {
                                backgroundColor: '#00468b !important',
                            },
                            '&:last-of-type': {
                                borderRight: 'none',
                            },
                        },
                        '& .MuiTab-root.Mui-selected': {
                            color: '#009BD8 !important',
                            backgroundColor: '#ffffff !important',
                            borderBottom: '9px solid #009BD8 !important',
                            borderRadius: '0 !important',
                            borderRight: 'none',
                            opacity: '1 !important',
                            '&:hover': {
                                backgroundColor: '#ffffff !important',
                            },
                        },
                        '& .MuiTab-root.Mui-disabled': {
                            color: '#8fa5bf !important',
                            backgroundColor: '#00254c !important',
                            opacity: '0.55 !important',
                            cursor: 'not-allowed !important',
                            pointerEvents: 'auto !important',
                            '&:hover': {
                                backgroundColor: '#00254c !important',
                            },
                        }
                    }}
                >
                    {tabLabels.map((label, index) => {
                        const disabled = isTabDisabled(index);
                        return (
                            <Tab
                                key={label}
                                disabled={disabled}
                                label={
                                    disabled ? (
                                        <Tooltip title={tabDisabledReason(index)}>
                                            <span>{label}</span>
                                        </Tooltip>
                                    ) : label
                                }
                            />
                        );
                    })}
                </Tabs>

                <Box sx={{ display: activeTab === 0 ? 'block' : 'none' }}>
                    <CreateSelectRuleTab
                        ref={createRuleRef}
                        onCompletionChange={setIsCreateRuleComplete}
                        onSavedRulesChange={setSavedRules}
                        onRuleSelect={setSelectedRuleId}
                        editRuleId={editRuleIdInTab0}
                        onClearEditRuleId={() => setEditRuleIdInTab0(null)}
                    />
                </Box>
                <Box sx={{ display: activeTab === 1 ? 'block' : 'none' }}>
                    <ServiceMappingTab
                        onCompletionChange={setIsServiceMappingComplete}
                        savedRules={savedRules}
                        mappings={mappings}
                        onMappingsChange={setMappings}
                        onSaveAndConfigure={(updatedMappings) => {
                            if (updatedMappings) setMappings(updatedMappings);
                            setIsServiceMappingComplete(true);
                            setActiveTab(CONFIGURE_RULES_TAB_INDEX);
                        }}
                    />
                </Box>
                <Box sx={{ display: activeTab === CONFIGURE_RULES_TAB_INDEX ? 'block' : 'none' }}>
                    <ConfigureRulesTab
                        ruleDetails={activeRuleDetails}
                        savedRules={savedRules}
                        mappings={mappings}
                        selectedRuleId={selectedRuleId}
                        onSelectRuleId={setSelectedRuleId}
                        onNavigateToServiceMapping={() => setActiveTab(1)}
                        onNavigateToTab={(tabIdx) => setActiveTab(tabIdx)}
                        onChangeRule={(ruleId) => {
                            setSelectedRuleId(ruleId);
                            setEditRuleIdInTab0(ruleId);
                            setActiveTab(0);
                        }}
                        onCanProceedChange={setCanProceedFromConfigureRules}
                        onRuleConfigSaved={(savedConfig) => {
                            if (savedConfig?._id) {
                                setSavedRules((prev) => {
                                    const matchedIdx = prev.findIndex((r) => r.id === selectedRuleId || (r.backendId && r.backendId === savedConfig._id));
                                    const targetIdx = matchedIdx !== -1 ? matchedIdx : prev.length - 1;
                                    return prev.map((r, idx) => {
                                        if (idx !== targetIdx) return r;
                                        const updatedStatus = savedConfig.Test_Run_Status || (r as any).Test_Run_Status || (r as any).testRunStatus || 'PASSED';
                                        return {
                                            ...r,
                                            backendId: savedConfig._id,
                                            resources: savedConfig.Resources || r.resources,
                                            schedule: savedConfig.Schedule || r.schedule,
                                            Test_Run_Status: updatedStatus,
                                            testRunStatus: updatedStatus
                                        };
                                    });
                                });
                            }
                        }}
                    />
                </Box>
                <Box sx={{ display: activeTab === APPROVAL_TAB_INDEX ? 'block' : 'none' }}>
                    <ApprovalTab
                        onStatusChange={setIsApprovalComplete}
                        onRequestChanges={() => setActiveTab(CONFIGURE_RULES_TAB_INDEX)}
                        savedRules={savedRules}
                        selectedRule={currentSelectedRule}
                        isActive={activeTab === APPROVAL_TAB_INDEX}
                        selectedRuleId={selectedRuleId}
                        onSelectRuleId={setSelectedRuleId}
                        onChangeRule={(ruleId: number) => {
                            setSelectedRuleId(ruleId);
                            setEditRuleIdInTab0(ruleId);
                            setActiveTab(0);
                        }}
                        onNavigateToTab={(tabIdx: number) => setActiveTab(tabIdx)}
                        mappings={mappings}
                    />
                </Box>
                <Box sx={{ display: activeTab === LAST_TAB_INDEX ? 'block' : 'none' }}>
                    <ActivationTab
                        savedRules={savedRules}
                        onActivationComplete={setIsActivationComplete}
                    />
                </Box>
            </Box>

            <Snackbar
                open={saveConfirmationOpen}
                autoHideDuration={4000}
                onClose={() => setSaveConfirmationOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSaveConfirmationOpen(false)}
                    severity="success"
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    Rule Engine setup complete and saved successfully.
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default EarlyWarningSystemRule;
